import { useState } from "react";
import Modal from "@/components/Modal";
import { Button, ButtonDelete, ButtonEdit } from "@/components/ui/button";
import toast from "react-hot-toast";
import { Check, RotateCcw, Trash2, X } from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  useGetExpenseCategoriesQuery,
  useCreateExpenseCategoryMutation,
  useUpdateExpenseCategoryMutation,
} from "@/redux/services/expense/expense.api";
import { ExpenseCategory } from "@/redux/services/expense/expense.type";
import { getExpenseCategoryLabel } from "@/lib/expenses";
import { getApiErrorMessage } from "@/lib/apiErrors";

interface ExpenseCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
}

const errorMessage = (res: { error?: unknown }) =>
  getApiErrorMessage(res.error);

const ExpenseCategoriesModal = ({
  isOpen,
  onClose,
  restaurantId,
}: ExpenseCategoriesModalProps) => {
  const { data: categories = [] } = useGetExpenseCategoriesQuery({
    restaurantId,
  });
  const [createCategory, { isLoading: isCreating }] =
    useCreateExpenseCategoryMutation();
  const [updateCategory] = useUpdateExpenseCategoryMutation();
  const confirm = useConfirm();

  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");

  const handleCreate = async () => {
    const label = newLabel.trim();
    if (!label) return;
    const res = await createCategory({ restaurantId, data: { label } });
    if ("data" in res) {
      toast.success("Catégorie ajoutée");
      setNewLabel("");
    } else {
      toast.error(errorMessage(res));
    }
  };

  const handleRename = async (category: ExpenseCategory) => {
    const label = editLabel.trim();
    if (!label) return;
    const res = await updateCategory({
      categoryUuid: category.id,
      data: { label },
    });
    if ("data" in res) {
      toast.success("Catégorie renommée");
      setEditingId(null);
    } else {
      toast.error(errorMessage(res));
    }
  };

  // "Delete" is a soft archive — the category leaves the list and the expense
  // form, but existing expenses keep it and it stays in historical breakdowns.
  const handleDelete = async (category: ExpenseCategory) => {
    const ok = await confirm({
      title: `Supprimer « ${getExpenseCategoryLabel(category)} » ?`,
      description:
        "La catégorie sera retirée de la liste et du formulaire de dépense. Les dépenses déjà enregistrées la conservent, et vous pourrez la restaurer.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    const res = await updateCategory({
      categoryUuid: category.id,
      data: { archived: true },
    });
    if ("data" in res) {
      toast.success("Catégorie supprimée");
    } else {
      toast.error(errorMessage(res));
    }
  };

  const handleRestore = async (category: ExpenseCategory) => {
    const res = await updateCategory({
      categoryUuid: category.id,
      data: { archived: false },
    });
    if ("data" in res) {
      toast.success("Catégorie restaurée");
    } else {
      toast.error(errorMessage(res));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Gérer les catégories de dépenses"
      size="large"
    >
      <div className="space-y-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleCreate();
              }
            }}
            className="flex-1 border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="Nouvelle catégorie (ex: Location moto-taxi)"
          />
          <Button
            type="button"
            size="sm"
            isLoading={isCreating}
            onClick={handleCreate}
          >
            Ajouter
          </Button>
        </div>

        <ul className="divide-y divide-border max-h-80 overflow-y-auto">
          {categories.map((category) => {
            const isSystem = category.type === "system";
            const isEditing = editingId === category.id;
            return (
              <li
                key={category.id}
                className="flex items-center justify-between gap-2 py-2"
              >
                {isEditing ? (
                  <input
                    autoFocus
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleRename(category);
                      }
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    className="flex-1 border border-border rounded-md px-2 py-1 text-sm"
                  />
                ) : (
                  <span
                    className={`text-sm ${category.archived ? "text-muted-foreground line-through" : "text-card-foreground"}`}
                  >
                    {getExpenseCategoryLabel(category)}
                    <span className="ml-2 text-xs text-muted-foreground no-underline">
                      {isSystem
                        ? "Système"
                        : category.archived
                          ? "Supprimée"
                          : "Personnalisé"}
                    </span>
                  </span>
                )}

                <div className="flex items-center gap-1">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        className="p-1.5 rounded hover:bg-muted"
                        onClick={() => handleRename(category)}
                        aria-label="Enregistrer"
                      >
                        <Check className="h-4 w-4 text-primary" />
                      </button>
                      <button
                        type="button"
                        className="p-1.5 rounded hover:bg-muted"
                        onClick={() => setEditingId(null)}
                        aria-label="Annuler"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    // System categories are the fixed defaults: read-only, no
                    // rename or delete. Only custom categories can be managed.
                    !isSystem &&
                    (category.archived ? (
                      <button
                        type="button"
                        title="Restaurer"
                        className="h-7 w-7 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors"
                        onClick={() => handleRestore(category)}
                      >
                        <RotateCcw className="h-4 w-4 text-gray-600" />
                      </button>
                    ) : (
                      <>
                        <ButtonEdit
                          onClick={() => {
                            setEditingId(category.id);
                            setEditLabel(category.label ?? "");
                          }}
                        />
                        <ButtonDelete onClick={() => handleDelete(category)} />
                      </>
                    ))
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
};

export default ExpenseCategoriesModal;
